import MiniSearch from 'minisearch'
import { useEffect, useMemo, useRef, useState } from 'react'
import { stemmer } from 'stemmer'

export interface MatchInfo {
  /* Stemmed query terms that matched — pass to `highlightMatches`. */
  terms: string[]
  score: number
}

export interface ClientSideSearchResult<T> {
  items: T[]
  matchById: Map<string, MatchInfo>
}

/* Module-level Porter stem cache. `stemmer()` is a pure function and
 * medical English vocabulary is bounded (~few thousand distinct word
 * forms across all participants). Caching halves stem cost during
 * search keystrokes — measured ~250ms → ~40ms on the worst-case
 * 609-doc patient. Capped at 50k entries and cleared wholesale on
 * overflow — transcriptions contain many unique one-shot tokens (MRNs,
 * CPT/ICD codes, UUID fragments) that can pollute the cache unbounded. */
const STEM_CACHE_MAX = 50_000
const stemCache = new Map<string, string>()

/* MiniSearch's default tokenizer split (whitespace + Unicode punctuation).
 * MiniSearch keeps both the regex and the splitter internal, so we re-declare
 * it here as the canonical default that custom `tokenize` overrides can fall
 * back to. */
const SPACE_OR_PUNCTUATION = /[\n\r\p{Z}\p{P}]+/u

export function defaultTokenize(text: string): string[] {
  return text.split(SPACE_OR_PUNCTUATION).filter(Boolean)
}

export interface UseClientSideSearchOptions<T> {
  fields: string[]
  boost: Record<string, number>
  extractFields: (item: T) => Record<string, string>
  /* When true, defer MiniSearch index construction to `requestIdleCallback`
   * (with a `setTimeout(0)` fallback for Safari). Queries issued before
   * the idle build fires trigger a synchronous build on demand. Consumers
   * can read `isIndexing` to render a "preparing search…" placeholder. */
  idleBuild?: boolean
  /* Override MiniSearch's tokenizer. Applied to both indexed fields (with the
   * field name as the second arg) and queries (field name `undefined`). When
   * omitted, MiniSearch's default whitespace/punctuation split is used.
   * Must be referentially stable — it is not part of the index rebuild key. */
  tokenize?: (text: string, fieldName?: string) => string[]
}

export interface UseClientSideSearchReturn<T> {
  search: (query: string) => ClientSideSearchResult<T>
  /* True while an `idleBuild` index is pending; always false for the sync path. */
  isIndexing: boolean
  /* Pre-extracted (HTML-stripped) field map captured once at index build.
   * Reused across renders so callers avoid re-running `extractTextFromHTML`
   * (fresh `DOMParser`) per cell. `undefined` for unknown ids or before
   * the first idle build lands. */
  getExtractedFields: (id: string) => Record<string, string> | undefined
}

/* Shared fold + Porter-v1-stem pipeline used by:
 *  - MiniSearch `processTerm` (indexed text + query tokens)
 *  - the proximity post-filter (tokenize-and-stem of indexed fields)
 *  - `highlightMatches` (whole-word stem compare)
 *
 * Pipeline: lowercase → NFC → NFD + strip combining marks → Porter stem.
 * Falsy input returns empty string (MiniSearch treats empty processTerm
 * output as "skip this token"). */
export function foldAndStem(term: string): string {
  if (!term) {
    return ''
  }
  const cached = stemCache.get(term)
  if (cached !== undefined) {
    return cached
  }
  const folded = term
    .toLowerCase()
    .normalize('NFC')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
  const stemmed = stemmer(folded)
  if (stemCache.size >= STEM_CACHE_MAX) {
    stemCache.clear()
  }
  stemCache.set(term, stemmed)
  return stemmed
}

export function _getStemCacheForTesting(): Map<string, string> {
  return stemCache
}

/* In-memory full-text search over `data` using MiniSearch (inverted
 * index + BM25 + prefix/fuzzy matching) with a Porter-stem + diacritic-
 * fold token pipeline. Replaces the prior substring `.includes()`
 * pattern on the visit notes and documents pages.
 *
 * Configuration:
 * - `processTerm`: fold + Porter-v1 stem for both indexed and query
 *   terms. So "diabetes" ≈ "diabetic" (both → "diabet"); "Hépatite" ≈
 *   "hepatite".
 * - `fuzzy`: disabled for terms <5 chars; exactly 1 absolute edit
 *   otherwise. Length here is *post-stemming* — MiniSearch invokes
 *   `fuzzy(term)` with the already-processed token. Morphology is
 *   handled by the stemmer; fuzzy is strictly typo insurance.
 * - `prefix`: only applied for terms ≥4 chars *post-stemming* (same —
 *   MiniSearch passes the processed token to the prefix predicate).
 * - `combineWith: 'AND'`: multi-word queries require all tokens.
 * - Post-process proximity filter: for 2+ token queries, a doc is only
 *   kept if at least two *different* query stems occur within ≤2
 *   intervening tokens of each other in some indexed field.
 *
 * The index is rebuilt when `data`, `fields`, or `boost` change. Callers
 * must stabilize `extractFields` themselves (wrap in `useCallback` with
 * deps) — the rebuild memo does NOT depend on its identity, so swapping
 * extractor implementations without also changing `data`/`fields`/`boost`
 * will not trigger a rebuild.
 *
 * - Empty queries return `data` unchanged with an empty match map, so
 *   callers can preserve their column-sort ordering.
 * - Non-empty queries return items sorted by BM25 relevance. */
interface ClientSideSearchIndex<T> {
  miniSearch: MiniSearch<Record<string, string> & { id: string }>
  byId: Map<string, T>
  docs: Map<string, Record<string, string>>
}

export function useClientSideSearch<T extends { id: string }>(
  data: T[] | undefined,
  options: UseClientSideSearchOptions<T>
): UseClientSideSearchReturn<T> {
  /* Live options in a ref so the index-build memo can read the current
   * fields/boost/extractFields without listing them as dependencies
   * (callers typically pass inline literals whose identity changes every
   * render). Rebuilds are keyed off `data` reference plus a deep hash of
   * fields+boost so genuine config changes still trigger a rebuild;
   * `extractFields` identity is explicitly NOT part of the key — callers
   * must stabilize it themselves (see JSDoc above). */
  const optionsRef = useRef(options)
  optionsRef.current = options

  const optionsKey = JSON.stringify({
    fields: options.fields,
    boost: options.boost,
  })

  const idleBuild = options.idleBuild === true

  /* Lazy factory shared by the sync and idle build paths. */
  const buildIndex = useMemo(
    () => (): ClientSideSearchIndex<T> => {
      const current = optionsRef.current
      const byId = new Map<string, T>()
      const docs = new Map<string, Record<string, string>>()
      interface TokenizeOption {
        tokenize?: typeof current.tokenize
      }
      const tokenizeOption: TokenizeOption = {}
      if (current.tokenize) {
        tokenizeOption.tokenize = current.tokenize
      }
      const ms = new MiniSearch<Record<string, string> & { id: string }>({
        fields: current.fields,
        storeFields: ['id'],
        idField: 'id',
        ...tokenizeOption,
        processTerm: (term) => {
          const stemmed = foldAndStem(term)
          return stemmed.length > 0 ? stemmed : null
        },
        searchOptions: {
          prefix: (term) => term.length >= 4,
          fuzzy: (term) => (term.length < 5 ? false : 1),
          combineWith: 'AND',
          boost: current.boost,
        },
      })

      if (data && data.length > 0) {
        const records = data.map((item) => {
          byId.set(item.id, item)
          const fields = current.extractFields(item)
          docs.set(item.id, fields)
          return { id: item.id, ...fields }
        })
        ms.addAll(records)
      }

      return { miniSearch: ms, byId, docs }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- optionsKey is a deep-equality hash of fields+boost that must drive rebuilds; options are read via ref
    [data, optionsKey]
  )

  /* Holds the built index when idleBuild is enabled. When null, the
   * index has not been built yet and `search()` must build synchronously
   * on first call. */
  const [idleIndex, setIdleIndex] = useState<ReturnType<
    typeof buildIndex
  > | null>(null)

  /* Persists the most recently built `docs` map (id → extracted fields)
   * across data changes, so `getExtractedFields` can keep returning
   * cached HTML-stripped text while the new idle index is still building.
   * Without this, every cell would fall back to a fresh
   * `extractTextFromHTML` (DOMParser) pass on the first paint after any
   * data mutation — the exact worst case. Stale entries are harmless:
   * callers look up by current row id, and unknown ids return `undefined`
   * which the fallback handles. Used only by the idle path. */
  const previousDocsRef = useRef<Map<string, Record<string, string>> | null>(
    null
  )

  const [isIndexing, setIsIndexing] = useState<boolean>(idleBuild)

  /* Handle to the pending idle callback / setTimeout so a
   * search-triggered synchronous fallback can cancel the queued work and
   * flip `isIndexing` immediately, instead of leaving the UI stuck on
   * "preparing search…" while results render. */
  const pendingIdleRef = useRef<{
    cancel: () => void
  } | null>(null)

  /* Holds an index built synchronously by `search()`'s fallback branch.
   * Staged in a ref rather than committed with a setter because that
   * branch runs during render, and a render-phase `setIdleIndex` is what
   * makes the reset effect below self-perpetuate into an unbounded loop
   * (see the commit effect). A `useEffect` commits it after the render.
   * The reset effect discards a staged build whenever `buildIndex`
   * changes, since it was built against the previous docs. */
  const syncFallbackRef = useRef<ReturnType<typeof buildIndex> | null>(null)

  /* Last `buildIndex` the reset effect ran for, so it can tell a real
   * rebuild-key change from its own first (or StrictMode-doubled) run.
   * Without this the effect would discard a build staged during the mount
   * render — the cold-load-with-a-query path — leaving `isIndexing` stuck
   * on until the idle callback fires and rebuilding the index for nothing. */
  const lastResetKeyRef = useRef(buildIndex)

  /* Reset the pending idleIndex when inputs change so `search()` doesn't
   * return matches from the stale inverted index. The `previousDocsRef`
   * above preserves the extracted-fields map across this window so
   * `getExtractedFields` keeps returning cached plaintext. */
  useEffect(() => {
    if (!idleBuild) {
      return
    }
    setIdleIndex((prev) => {
      if (prev) {
        previousDocsRef.current = prev.docs
      }
      return null
    })
    setIsIndexing(true)
    if (lastResetKeyRef.current !== buildIndex) {
      syncFallbackRef.current = null
    }
    lastResetKeyRef.current = buildIndex

    const ric =
      typeof window !== 'undefined' &&
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : null
    const cancel =
      typeof window !== 'undefined' &&
      typeof window.cancelIdleCallback === 'function'
        ? window.cancelIdleCallback
        : null

    let cancelled = false
    const run = (): void => {
      if (cancelled) {
        return
      }
      pendingIdleRef.current = null
      const built = buildIndex()
      previousDocsRef.current = null
      setIdleIndex(built)
      setIsIndexing(false)
    }

    if (ric) {
      const handle = ric(run, { timeout: 2000 })
      pendingIdleRef.current = {
        cancel: () => {
          cancelled = true
          if (cancel) {
            cancel(handle)
          }
        },
      }
      return () => {
        cancelled = true
        if (cancel) {
          cancel(handle)
        }
        pendingIdleRef.current = null
      }
    }
    const handle = setTimeout(run, 0)
    pendingIdleRef.current = {
      cancel: () => {
        cancelled = true
        clearTimeout(handle)
      },
    }
    return () => {
      cancelled = true
      clearTimeout(handle)
      pendingIdleRef.current = null
    }
  }, [buildIndex, idleBuild])

  /* Synchronous (default) build — memoized on the same keys as
   * `buildIndex`. When idleBuild is on, we lazily build on first use
   * if the idle callback hasn't fired yet. */
  const syncIndex = useMemo(() => {
    if (idleBuild) {
      return null
    }
    return buildIndex()
  }, [buildIndex, idleBuild])

  /* Commits a synchronously-built fallback index (staged in the ref above
   * by `search()`'s render-phase fallback branch) into real state. Runs
   * after every render rather than depending on the ref (refs are never
   * valid effect deps); the ref-vs-state identity check makes it a no-op
   * on renders where nothing new was staged.
   *
   * Deferring the commit to here — rather than setting state straight from
   * `search()` — is what bounds the update loop. The reset effect above is
   * declared first, so on any render where `buildIndex` churned it runs
   * first and drops the staged build; this effect then finds nothing to
   * commit and schedules no update, and the cycle stops on its own. A
   * render-phase setter could not be pre-empted that way: it re-populated
   * `idleIndex` on the very next render, which handed the reset effect a
   * fresh key to null out again, forever. */
  useEffect(() => {
    if (syncFallbackRef.current && syncFallbackRef.current !== idleIndex) {
      setIdleIndex(syncFallbackRef.current)
      setIsIndexing(false)
    }
  })

  const result = useMemo(
    () => ({
      getExtractedFields: (id: string): Record<string, string> | undefined => {
        const index = syncIndex ?? idleIndex ?? syncFallbackRef.current
        const current = index?.docs.get(id)
        if (current !== undefined) {
          return current
        }
        /* Fall back to the previous build's docs map during the idle
         * rebuild window. Returning stale plaintext is strictly better
         * than forcing `extractTextFromHTML` to run per-cell on every
         * render until the new index lands. */
        return previousDocsRef.current?.get(id)
      },
      search: (query: string): ClientSideSearchResult<T> => {
        const trimmed = query.trim()
        if (!trimmed) {
          return { items: data ?? [], matchById: new Map() }
        }
        let index = syncIndex ?? idleIndex ?? syncFallbackRef.current
        if (!index) {
          /* Synchronous fallback: idle callback hasn't fired yet but the
           * user issued a query. Build now and stage into the ref — every
           * consumer calls `search()` from inside a render-body `useMemo`,
           * so committing to state here would be a render-phase update the
           * reset effect can't pre-empt (see the commit effect above). The
           * commit effect moves this into `idleIndex` after the render
           * completes; until then the ref is read as a third fallback
           * everywhere `idleIndex` is read. */
          index = buildIndex()
          syncFallbackRef.current = index
          if (pendingIdleRef.current) {
            pendingIdleRef.current.cancel()
            pendingIdleRef.current = null
          }
          previousDocsRef.current = null
        }
        const { miniSearch, byId, docs } = index
        const results = miniSearch.search(trimmed)

        /* Query tokens for proximity filtering. Tokenize via the same
         * Unicode word regex MiniSearch uses (`[\p{L}\p{N}]+`), NOT a
         * whitespace split — otherwise hyphenated queries like
         * "blood-pressure" collapse to one token, the multi-word gate
         * stays false, and the proximity filter is silently skipped. */
        const queryStems = (trimmed.match(PROXIMITY_TOKEN_RE) ?? [])
          .map((t) => foldAndStem(t))
          .filter((s) => s.length > 0)
        const isMultiWord = queryStems.length >= 2

        const items: T[] = []
        const matchById = new Map<string, MatchInfo>()
        for (const r of results) {
          const id = String(r.id)
          const item = byId.get(id)
          if (!item) {
            continue
          }

          if (isMultiWord) {
            const fieldValues = docs.get(id)
            if (!fieldValues || !hasProximityPair(fieldValues, queryStems)) {
              continue
            }
          }

          items.push(item)
          matchById.set(item.id, {
            terms: r.terms ?? [],
            score: r.score ?? 0,
          })
        }
        return { items, matchById }
      },
    }),
    /* `isIndexing` deliberately omitted: it flips during index construction
     * and would otherwise rebuild the returned `search` identity mid-type,
     * cascading memo invalidations that drop keystrokes. The sync-fallback
     * branch inside `search()` already handles the !index case directly. */
    [syncIndex, idleIndex, buildIndex, data]
  )

  return { ...result, isIndexing }
}

/* MiniSearch's default tokenizer splits on Unicode whitespace + common
 * punctuation; we match that behavior for the proximity filter so the
 * stream of tokens we inspect matches what MiniSearch indexed. */
const PROXIMITY_TOKEN_RE = /[\p{L}\p{N}]+/gu

/* A doc passes the proximity filter if at least two *different* query
 * stems occur within ≤2 intervening tokens of each other in any
 * indexed field. Hard-drops documents where query terms are scattered
 * far apart. Single-word queries skip this check entirely (caller
 * gates).
 *
 * A document token stem `s` matches a query stem `q` if `s === q` OR
 * (`q.length >= 4` AND `s.startsWith(q)`). This mirrors MiniSearch's
 * prefix-expansion gate (`prefix: (term) => term.length >= 4`) so that
 * documents legitimately matched via prefix (e.g. query "diab" hitting
 * indexed "diabet") aren't dropped here by a stricter equality check. */
function hasProximityPair(
  fields: Record<string, string>,
  queryStems: string[]
): boolean {
  const uniqueStems = new Set(queryStems)
  if (uniqueStems.size < 2) {
    return true
  }
  const uniqueStemsArr = Array.from(uniqueStems)
  const prefixEligible = uniqueStemsArr.filter((q) => q.length >= 4)
  const matchQueryStem = (docStem: string): string | null => {
    if (uniqueStems.has(docStem)) {
      return docStem
    }
    for (const q of prefixEligible) {
      if (docStem.startsWith(q)) {
        return q
      }
    }
    return null
  }

  for (const text of Object.values(fields)) {
    if (!text) {
      continue
    }
    /* Build an array of { matchedQueryStemOrNull, index } for each
     * tokenized word, then walk the stream tracking the most recent
     * position of each query stem (keyed by the QUERY stem, not the
     * doc token stem, so prefix-expanded matches share a bucket). If
     * any two *different* query stems are within 3 tokens (≤2
     * intervening), we're done. */
    const tokens: (string | null)[] = []
    const matches = text.match(PROXIMITY_TOKEN_RE) ?? []
    for (const tok of matches) {
      const s = foldAndStem(tok)
      tokens.push(matchQueryStem(s))
    }
    const lastSeen = new Map<string, number>()
    for (let i = 0; i < tokens.length; i++) {
      const s = tokens[i]
      if (!s) {
        continue
      }
      for (const [otherStem, otherIdx] of lastSeen) {
        if (otherStem !== s && i - otherIdx <= 3) {
          return true
        }
      }
      lastSeen.set(s, i)
    }
  }
  return false
}

/* Count how many whole-word tokens in `text` stem to one of
 * `stemmedTerms`. Uses the same tokenization and fold+stem pipeline as
 * the search index, so counts here match what MiniSearch scored.
 *
 * - Empty `text` or empty `stemmedTerms` yields 0.
 * - Each matching token counts as 1 (repeated words count separately). */
export function countStemOccurrences(
  text: string,
  stemmedTerms: string[]
): number {
  if (!text || stemmedTerms.length === 0) {
    return 0
  }
  const stemSet = new Set(stemmedTerms.filter(Boolean))
  if (stemSet.size === 0) {
    return 0
  }
  let count = 0
  for (const m of text.matchAll(/[\p{L}\p{N}]+/gu)) {
    if (stemSet.has(foldAndStem(m[0]))) {
      count += 1
    }
  }
  return count
}

export interface CountStemOccurrencesMultiResult {
  total: number
  perField: Record<string, number>
}

/* Single-pass variant of `countStemOccurrences` that walks each
 * field text once and returns both per-field counts and a grand total.
 * Equivalent to summing N independent `countStemOccurrences` calls but
 * shares the empty-checks and stem-set construction. Saves N-1 redundant
 * `Set` constructions and short-circuits on empty terms once. Pair with
 * the hook's `getExtractedFields(id)` to feed already-HTML-stripped
 * fields. */
export function countStemOccurrencesMulti(
  texts: Record<string, string>,
  stemmedTerms: string[]
): CountStemOccurrencesMultiResult {
  const perField: Record<string, number> = {}
  if (stemmedTerms.length === 0) {
    for (const key of Object.keys(texts)) {
      perField[key] = 0
    }
    return { total: 0, perField }
  }
  const stemSet = new Set(stemmedTerms.filter(Boolean))
  if (stemSet.size === 0) {
    for (const key of Object.keys(texts)) {
      perField[key] = 0
    }
    return { total: 0, perField }
  }
  let total = 0
  for (const [key, text] of Object.entries(texts)) {
    if (!text) {
      perField[key] = 0
      continue
    }
    let count = 0
    for (const m of text.matchAll(/[\p{L}\p{N}]+/gu)) {
      if (stemSet.has(foldAndStem(m[0]))) {
        count += 1
      }
    }
    perField[key] = count
    total += count
  }
  return { total, perField }
}

export interface SnippetPart {
  text?: string
  mark?: string
}

export interface SnippetResult {
  parts: SnippetPart[]
  matchesInWindow: number
  truncatedLeft: boolean
  truncatedRight: boolean
}

/* Build a ~`windowChars`-wide snippet of `text` centered on the first
 * token whose stem appears in `stemmedTerms`, with all matches inside
 * the window wrapped as `{ mark }` segments. Snaps window edges
 * outward to the nearest whitespace (or text edge) so words aren't cut
 * mid-character.
 *
 * Returns `null` when the input is empty, `stemmedTerms` is empty, or
 * no tokens match — callers can treat that as "no snippet to render."
 *
 * `truncatedLeft`/`truncatedRight` indicate whether the window starts
 * mid-text (prepend `…`) or ends before text end (append `…`).
 * `matchesInWindow` is the count of marks emitted; callers with a total
 * occurrence count can render "+N more matches" using the difference. */
export function buildSnippet(
  text: string,
  stemmedTerms: string[],
  options?: { windowChars?: number }
): SnippetResult | null {
  if (!text || stemmedTerms.length === 0) {
    return null
  }
  const stemSet = new Set(stemmedTerms.filter(Boolean))
  if (stemSet.size === 0) {
    return null
  }

  interface Token {
    index: number
    length: number
    original: string
    stem: string
    isMatch: boolean
  }
  const tokens: Token[] = []
  for (const m of text.matchAll(/[\p{L}\p{N}]+/gu)) {
    const start = m.index ?? 0
    const original = m[0]
    const stem = foldAndStem(original)
    tokens.push({
      index: start,
      length: original.length,
      original,
      stem,
      isMatch: stemSet.has(stem),
    })
  }

  const firstMatch = tokens.find((t) => t.isMatch)
  if (!firstMatch) {
    return null
  }

  const windowChars = options?.windowChars ?? 140
  const half = Math.floor(windowChars / 2)
  const matchStart = firstMatch.index
  const matchEnd = firstMatch.index + firstMatch.length

  let windowStart = Math.max(0, matchStart - half)
  let windowEnd = Math.min(text.length, matchEnd + half)

  /* Snap outward to whitespace (or text edge) — never shrink inward,
   * as that would risk cutting off the anchor match. */
  while (windowStart > 0 && !/\s/.test(text[windowStart - 1] ?? '')) {
    windowStart -= 1
  }
  while (windowEnd < text.length && !/\s/.test(text[windowEnd] ?? '')) {
    windowEnd += 1
  }

  const truncatedLeft = windowStart > 0
  const truncatedRight = windowEnd < text.length

  const parts: SnippetPart[] = []
  let cursor = windowStart
  let matchesInWindow = 0
  for (const t of tokens) {
    if (!t.isMatch) {
      continue
    }
    if (t.index < windowStart || t.index + t.length > windowEnd) {
      continue
    }
    if (t.index > cursor) {
      parts.push({ text: text.slice(cursor, t.index) })
    }
    parts.push({ mark: t.original })
    cursor = t.index + t.length
    matchesInWindow += 1
  }
  if (cursor < windowEnd) {
    parts.push({ text: text.slice(cursor, windowEnd) })
  }

  return { parts, matchesInWindow, truncatedLeft, truncatedRight }
}

/* Stems a raw query string the same way the index stems both indexed
 * text and query tokens, yielding terms suitable for
 * `findStemMatchRanges` / `highlightMatches`.
 *
 * The notes list gets its terms from `matchById` (the stems MiniSearch
 * actually scored per row). Surfaces that only carry the raw query —
 * the note detail page, which receives it through the URL — derive the
 * same stems here, so both highlight the same words. */
export function stemQueryTerms(query: string): string[] {
  const stems = new Set<string>()
  for (const m of query.matchAll(/[\p{L}\p{N}]+/gu)) {
    const stem = foldAndStem(m[0])
    if (stem) {
      stems.add(stem)
    }
  }
  return [...stems]
}

export interface StemMatchRange {
  /* Index of the first character of the matched word in `text`. */
  start: number
  /* Index one past the last character of the matched word. */
  end: number
}

/* Character ranges of every whole word in `text` whose stem is in
 * `stemmedTerms`. The single source of truth for "where does the query
 * match this string" — `highlightMatches` renders these as <mark>
 * segments for the notes list, and the editor's search-highlight
 * ProseMirror plugin maps them to document positions for the note body.
 * Both surfaces therefore mark exactly the same words.
 *
 * Whole-word stem comparison against the same fold + Porter-stem
 * pipeline and the same Unicode token regex the index itself uses, so
 * every MiniSearch hit has a corresponding visual highlight.
 *
 * - Pass `matchById.get(id)?.terms` directly — they're already stemmed.
 * - Case-insensitive and diacritic-insensitive by construction.
 * - No prefix wrapping: a "dia" query does not match "diabetes" unless
 *   the stems are equal; deliberate, and mirrors the ≥4-char prefix
 *   gate in the search itself. */
export function findStemMatchRanges(
  text: string,
  stemmedTerms: string[]
): StemMatchRange[] {
  if (!text || stemmedTerms.length === 0) {
    return []
  }
  const stemSet = new Set(stemmedTerms.filter(Boolean))
  if (stemSet.size === 0) {
    return []
  }
  const ranges: StemMatchRange[] = []
  /* Same token regex as the proximity filter — both consume the same
   * character classes, so what MiniSearch scores, we highlight. */
  for (const m of text.matchAll(/[\p{L}\p{N}]+/gu)) {
    const word = m[0]
    if (!stemSet.has(foldAndStem(word))) {
      continue
    }
    const start = m.index ?? 0
    ranges.push({ start, end: start + word.length })
  }
  return ranges
}

/* Wrap occurrences of `stemmedTerms` inside `text` with a
 * `{ mark: string }` marker so callers can render a <mark> element.
 * Returns a flat array of plain strings and match markers suitable for
 * mapping to JSX. Match positions come from `findStemMatchRanges`. */
export function highlightMatches(
  text: string,
  stemmedTerms: string[]
): (string | { mark: string })[] {
  const ranges = findStemMatchRanges(text, stemmedTerms)
  if (ranges.length === 0) {
    return [text]
  }

  const parts: (string | { mark: string })[] = []
  let lastIndex = 0
  for (const { start, end } of ranges) {
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start))
    }
    parts.push({ mark: text.slice(start, end) })
    lastIndex = end
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts
}
